import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Key, Eye, Trash2, Copy, RefreshCw, Shield, Clock } from 'lucide-react';

const DeveloperDashboard = () => {
  const apiKeys = [
    {
      id: 'key-001',
      name: 'Production API Key',
      key: 'sk-prod-...abc123',
      permissions: ['read', 'write'],
      lastUsed: '2025-01-15T10:30:00Z',
      status: 'active'
    },
    {
      id: 'key-002',
      name: 'Development API Key',
      key: 'sk-dev-...def456',
      permissions: ['read'],
      lastUsed: '2025-01-14T16:45:00Z',
      status: 'active'
    }
  ];

  const webhooks = [
    {
      id: 'webhook-001',
      name: 'Ticket Created Webhook',
      url: 'https://api.company.com/webhooks/tickets',
      events: ['ticket.created', 'ticket.updated'],
      status: 'active',
      lastTriggered: '2025-01-15T09:15:00Z'
    },
    {
      id: 'webhook-002',
      name: 'User Registration Webhook',
      url: 'https://api.company.com/webhooks/users',
      events: ['user.created'],
      status: 'inactive',
      lastTriggered: '2025-01-10T14:20:00Z'
    }
  ];

  const plugins = [
    {
      id: 'plugin-001',
      name: 'Slack Integration',
      version: '1.2.0',
      status: 'installed',
      description: 'Integrate with Slack for notifications'
    },
    {
      id: 'plugin-002',
      name: 'Jira Connector',
      version: '2.1.0',
      status: 'installed',
      description: 'Connect tickets with Jira issues'
    }
  ];

  return (
    <div className="w-full" style={{ backgroundColor: '#F5F7FA', margin: 0, padding: 0 }}>
      {/* Hero Section with Background */}
      <section className="relative py-6 sm:py-8 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ margin: 0 }}>
        {/* Muted background overlay */}
        <div className="absolute inset-0" style={{ backgroundColor: '#F8F9FA' }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-2 sm:mb-3" style={{ backgroundColor: 'rgba(72, 61, 133, 0.1)', border: '1px solid rgba(72, 61, 133, 0.2)' }}>
              <span className="text-xs sm:text-sm font-semibold" style={{ color: '#483D85' }}>Developer Tools, APIs & Integrations</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: '#252525', margin: 0 }}>
              Developer Dashboard
            </h1>
            <p className="text-sm sm:text-base lg:text-lg max-w-3xl mx-auto text-center" style={{ color: '#585858', margin: '0 auto', padding: 0 }}>
              Monitor integrations, APIs, and technical system configurations.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <div className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 relative" style={{ backgroundColor: '#F5F7FA', margin: 0, paddingTop: '1.5rem' }}>
        <div className="space-y-6">

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">API Keys</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{apiKeys.length}</p>
              </div>
              <Key className="h-8 w-8 text-primary-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Webhooks</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{webhooks.length}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Plugins</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{plugins.length}</p>
              </div>
              <Shield className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">API Calls Today</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">1,247</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Keys */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Key className="h-5 w-5" />
              <span>Recent API Keys</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div key={key.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                      {key.name}
                    </h3>
                    <Badge className={key.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}>
                      {key.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                    {key.key}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-1">
                      {key.permissions.map((perm) => (
                        <Badge key={perm} variant="secondary" className="text-xs">
                          {perm}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm">
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Copy size={16} />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Webhooks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <RefreshCw className="h-5 w-5" />
              <span>Active Webhooks</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {webhooks.map((webhook) => (
                <div key={webhook.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                      {webhook.name}
                    </h3>
                    <Badge className={webhook.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}>
                      {webhook.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                    {webhook.url}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-1">
                      {webhook.events.map((event) => (
                        <Badge key={event} variant="outline" className="text-xs">
                          {event}
                        </Badge>
                      ))}
                    </div>
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Last: {new Date(webhook.lastTriggered).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plugins */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>Installed Plugins</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plugins.map((plugin) => (
              <div key={plugin.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                    {plugin.name}
                  </h3>
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    {plugin.status}
                  </Badge>
                </div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                  {plugin.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    Version: {plugin.version}
                  </span>
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm">Configure</Button>
                    <Button variant="ghost" size="sm">Update</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
        </div>
      </div>
    </div>
  );
};

export default DeveloperDashboard;
